import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import Sidebar from './Sidebar';
import { getClientLogo, createClientLogo, updateClientLogo, deleteClientLogo } from '../api/auth/ClientLogoService';

function ClientLogo() {
    const [data, setData] = useState([]);
    const [values, setValues] = useState({ image: null });
    const [editingId, setEditingId] = useState(null);
    const fileInputRef = useRef(null);

    // Fetch client logos on mount
    useEffect(() => {
        fetchClientLogo();
    }, []);

    const fetchClientLogo = async () => {
        try {
            const response = await getClientLogo();
            setData(response);
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire('Error', error.message || 'Failed to fetch client logo', 'error');
        }
    };

    const handleChanges = (e) => {
        const { name, files } = e.target;
        if (name === 'image') {
            setValues((prev) => ({ ...prev, image: files[0] }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!values.image && !editingId) {
            Swal.fire('Validation Error', 'Please select an image.', 'warning');
            return;
        }

        const formData = new FormData();
        if (values.image) formData.append('image', values.image);
        formData.append('date', new Date().toISOString().split('T')[0]);

        try {
            if (editingId) {
                await updateClientLogo(editingId, formData);
                Swal.fire('Updated!', 'Client logo updated successfully!', 'success');
                setEditingId(null);
            } else {
                await createClientLogo(formData);
                Swal.fire('Added!', 'Client logo added successfully!', 'success');
            }

            setValues({ image: null });
            if (fileInputRef.current) fileInputRef.current.value = '';
            fetchClientLogo();
        } catch (error) {
            console.error('Error submitting data:', error);
            Swal.fire('Error', error.message || 'Failed to submit client logo', 'error');
        }
    };

    const handleEditClick = (item) => {
        setValues({ image: null }); // Can't pre-fill file input
        setEditingId(item.id);
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'This will permanently delete the client logo.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deleteClientLogo(id);
                    setData((prev) => prev.filter(item => item.id !== id));
                    Swal.fire('Deleted!', 'The client logo has been deleted.', 'success');
                } catch (error) {
                    Swal.fire('Error!', error.message || 'Failed to delete client logo', 'error');
                    console.error('Delete error:', error);
                }
            }
        });
    };

    return (
        <div>
            <Sidebar />
            <div className="col-md-9 px-0" style={{ marginLeft: '215px' }}>
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12">
                            <h5 className="page-title">{editingId ? 'Edit Client Logo' : 'Add Client Logo'}</h5>
                        </div>
                    </div>
                    <br />

                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="clientImage" className="form-label">Client Logo</label>
                            <input
                                type="file"
                                className="form-control"
                                id="clientImage"
                                accept="image/*"
                                name="image"
                                onChange={handleChanges}
                                ref={fileInputRef}
                            />
                        </div>

                        <button type="submit" className="btn btn-primary">
                            {editingId ? 'Update Logo' : 'Add Logo'}
                        </button>
                    </form>
                </div>
            </div>

            <div className="col-md-10 mt-5" style={{ marginLeft: '200px' }}>
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Logo</th>
                            <th>Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length > 0 ? (
                            data.map((item, index) => (
                                <tr key={item.id}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <img
                                            src={item.imageBytes ? `data:image/jpeg;base64,${item.imageBytes}` : 'https://via.placeholder.com/100'}
                                            alt="client logo"
                                            style={{ width: '100px', height: 'auto', borderRadius: '5px' }}
                                        />
                                    </td>
                                    <td>{item.date ?? 'N/A'}</td>
                                    <td>
                                        <button className="btn btn-primary me-2" onClick={() => handleEditClick(item)}>Edit</button>
                                        <button className="btn btn-danger" onClick={() => handleDelete(item.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="text-center">No Client Logo found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ClientLogo;
